import { useEffect, useState } from 'react';

/**
 * Whether a CSS media query currently matches.
 *
 * For the cases a Tailwind breakpoint cannot answer: a component that renders
 * into a portal escapes its parent, so `lg:hidden` on the wrapper hides
 * nothing and a drawer meant for phones opens on a desktop too. Deciding in
 * state rather than in CSS also means the two layouts are genuinely
 * exclusive — only one of them mounts, so only one can hold focus.
 *
 * Keep the query string a literal: a new string on every render would
 * resubscribe on every render.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches
  );

  useEffect(() => {
    const list = window.matchMedia(query);
    // Read once on subscribe: the query may have changed between the initial
    // state and this effect, and a resize is not guaranteed to follow.
    setMatches(list.matches);

    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
